<template>
  <section aria-labelledby="map-canvas-title" class="flex h-full min-h-0 min-w-0 flex-col">
    <div class="mb-2 flex shrink-0 items-center justify-between gap-3">
      <div>
        <h3 id="map-canvas-title" class="text-sm font-semibold text-text-primary">{{ messages.canvas.title }}</h3>
        <p id="map-pan-help" class="text-xs text-text-muted">
          {{ editable ? messages.canvas.helpEditable : `${messages.canvas.helpViewOnlyLeadIn} ${messages.canvas.helpViewOnly}` }}
        </p>
      </div>
      <span v-if="editable" class="font-mono text-2xs uppercase tracking-wide text-accent">{{ messages.canvas.officerEditBadge }}</span>
    </div>

    <div
      class="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-lg border border-border-strong bg-bg"
      role="region"
      :aria-label="messages.canvas.regionLabel"
      aria-describedby="map-pan-help"
    >
      <div
        ref="mapRef"
        class="relative isolate aspect-map h-full max-w-full"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointercancel="cancelDrag"
      >
        <picture class="block size-full">
          <source
            type="image/avif"
            srcset="/assets/maps/gvg-map-900.avif 900w, /assets/maps/gvg-map-1350.avif 1350w, /assets/maps/gvg-map-1798.avif 1798w"
            sizes="(min-width: 80rem) 75vw, (min-width: 64rem) 75vw, (max-width: 48rem) 48rem, 100vw"
          />
          <source
            type="image/webp"
            srcset="/assets/maps/gvg-map-900.webp 900w, /assets/maps/gvg-map-1350.webp 1350w, /assets/maps/gvg-map-1798.webp 1798w"
            sizes="(min-width: 80rem) 75vw, (min-width: 64rem) 75vw, (max-width: 48rem) 48rem, 100vw"
          />
          <img
            src="/assets/maps/gvg-map.png"
            width="1893"
            height="1449"
            :alt="messages.canvas.imageAlt"
            class="block size-full select-none"
            draggable="false"
          />
        </picture>

        <GvgMapTagMarker
          v-for="location in locations"
          :key="location.key"
          :objective="location"
          :player-names="playerNamesForTag(location.tagKey)"
          :editable="editable"
          @edit="editTag(location.tagKey)"
        />
        <GvgMapTagMarker
          :objective="treeObjective"
          :player-names="playerNamesForTag(TREE_OBJECTIVE.tagKey)"
          :editable="editable"
          :movable="editable"
          kind="tree"
          @move-start="startTreeDrag"
          @nudge="onTreeNudge"
          @edit="editTag(TREE_OBJECTIVE.tagKey)"
        />
        <GvgMapTagMarker
          v-for="objective in bossObjectives"
          :key="objective.key"
          :objective="objective"
          :player-names="playerNamesForTag(objective.tagKey)"
          :editable="editable"
          kind="boss"
          @edit="editTag(objective.tagKey)"
        />

        <GvgMapGroupMarker
          v-for="group in groups"
          :key="group.id"
          :group="group"
          :position="displayPosition(group)"
          :player-names="playerNamesForGroup(group)"
          :editable="editable"
          @move-start="startGroupDrag(group.id, $event)"
          @nudge="onNudge(group.id, $event)"
          @edit="editGroup(group)"
        />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import { BOSS_OBJECTIVES, FOREST_LOCATIONS, TREE_OBJECTIVE } from '../../lib/gvgMap'
import { playerDisplayName } from '../../lib/playerIdentity'
import type {
  MapTagObjective,
  Player,
  PlayerNameMode,
  RaidGroup,
  RaidMapGroupPosition,
  RaidMapTreePosition,
} from '../../types'
import GvgMapGroupMarker from './GvgMapGroupMarker.vue'
import type { MapMarkerNudge } from './GvgMapGroupMarker.vue'
import GvgMapTagMarker from './GvgMapTagMarker.vue'
import { gvgMapEn } from './locales/en.ts'
import { gvgMapRu } from './locales/ru.ts'

export interface GvgMapCanvasProps {
  readonly groups: ReadonlyArray<RaidGroup>
  /** Raid-aware players, hydrated with their live guild tags. */
  readonly players: ReadonlyArray<Player>
  readonly playerNameMode: PlayerNameMode
  readonly positions: ReadonlyArray<RaidMapGroupPosition>
  readonly treePosition: RaidMapTreePosition | null
  /** Seeded Defense groups use the allied left-side formation. */
  readonly defenseGroupIds: ReadonlySet<string>
  /** Seeded Attack groups use the enemy right-side formation. */
  readonly attackGroupIds: ReadonlySet<string>
  readonly editable: boolean
}

interface MapPosition {
  readonly xBasisPoints: number
  readonly yBasisPoints: number
}

interface GroupDragState {
  readonly kind: 'group'
  readonly raidGroupId: string
  readonly pointerId: number
  readonly initial: RaidMapGroupPosition
  readonly preview: RaidMapGroupPosition
}

interface TreeDragState {
  readonly kind: 'tree'
  readonly pointerId: number
  readonly initial: RaidMapTreePosition
  readonly preview: RaidMapTreePosition
}

type DragState = GroupDragState | TreeDragState

const props = defineProps<GvgMapCanvasProps>()

const emit = defineEmits<{
  (event: 'move-group', raidGroupId: string, xBasisPoints: number, yBasisPoints: number): void
  (event: 'move-tree', xBasisPoints: number, yBasisPoints: number): void
  (event: 'edit-group', groupStableId: string): void
  (event: 'edit-tag', tagKey: string): void
}>()

const messages = useLocaleMessages(defineLocaleCatalog({ en: gvgMapEn, ru: gvgMapRu }))

const locations = FOREST_LOCATIONS
const bossObjectives = BOSS_OBJECTIVES
const mapRef = ref<HTMLElement | null>(null)
const dragState = ref<DragState | null>(null)

const DEFAULT_DEFENSE_POSITION_X = 2200
const DEFAULT_ATTACK_POSITION_X = 7800
const DEFAULT_NEUTRAL_POSITION_MAX_COLUMNS = 4
const DEFAULT_NEUTRAL_POSITION_MIN_X = 1500
const DEFAULT_NEUTRAL_POSITION_MAX_X = 8500
const DEFAULT_POSITION_MIN_Y = 2000
const DEFAULT_POSITION_MAX_Y = 8000
const DRAG_SAVE_THRESHOLD_BASIS_POINTS = 20

const positionsByGroupId = computed<ReadonlyMap<string, RaidMapGroupPosition>>(
  () => new Map(props.positions.map((position) => [position.raidGroupId, position])),
)

const treeObjective = computed<MapTagObjective>(() => {
  const position = displayTreePosition()
  return {
    ...TREE_OBJECTIVE,
    xBasisPoints: position.xBasisPoints,
    yBasisPoints: position.yBasisPoints,
  }
})

const playerNamesByTagKey = computed<ReadonlyMap<string, ReadonlyArray<string>>>(() => {
  const namesByTagKey = new Map<string, ReadonlyArray<string>>()
  for (const player of props.players) {
    const playerName = playerDisplayName(player, props.playerNameMode)
    for (const tag of player.tags) {
      const tagKey = tag.seedKey ?? ''
      if (tagKey === '') continue
      const names = namesByTagKey.get(tagKey) ?? []
      if (!names.includes(playerName)) namesByTagKey.set(tagKey, [...names, playerName])
    }
  }

  const sortedNamesByTagKey = new Map<string, ReadonlyArray<string>>()
  for (const [tagKey, names] of namesByTagKey) {
    const sortedNames = names.slice()
    sortedNames.sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }))
    sortedNamesByTagKey.set(tagKey, sortedNames)
  }
  return sortedNamesByTagKey
})

function clampBasisPoints(value: number): number {
  return Math.min(10000, Math.max(0, Math.round(value)))
}

function spreadCoordinate(index: number, count: number, minimum: number, maximum: number): number {
  if (count <= 1) return Math.round((minimum + maximum) / 2)
  return Math.round(minimum + ((maximum - minimum) * index) / (count - 1))
}

function linePosition(
  group: RaidGroup,
  groupIds: ReadonlySet<string>,
  xBasisPoints: number,
): RaidMapGroupPosition {
  const lineGroups = props.groups.filter((candidate) => groupIds.has(candidate.stableId))
  const index = Math.max(0, lineGroups.findIndex((candidate) => candidate.id === group.id))
  return {
    raidGroupId: group.id,
    xBasisPoints,
    yBasisPoints: spreadCoordinate(index, lineGroups.length, DEFAULT_POSITION_MIN_Y, DEFAULT_POSITION_MAX_Y),
  }
}

function neutralPosition(group: RaidGroup): RaidMapGroupPosition {
  const neutralGroups = props.groups.filter((candidate) =>
    !props.defenseGroupIds.has(candidate.stableId) && !props.attackGroupIds.has(candidate.stableId),
  )
  const index = Math.max(0, neutralGroups.findIndex((candidate) => candidate.id === group.id))
  const row = Math.floor(index / DEFAULT_NEUTRAL_POSITION_MAX_COLUMNS)
  const rowCount = Math.max(1, Math.ceil(neutralGroups.length / DEFAULT_NEUTRAL_POSITION_MAX_COLUMNS))
  const rowStartIndex = row * DEFAULT_NEUTRAL_POSITION_MAX_COLUMNS
  const column = index - rowStartIndex
  const columnsInRow = Math.min(DEFAULT_NEUTRAL_POSITION_MAX_COLUMNS, neutralGroups.length - rowStartIndex)
  return {
    raidGroupId: group.id,
    xBasisPoints: spreadCoordinate(column, columnsInRow, DEFAULT_NEUTRAL_POSITION_MIN_X, DEFAULT_NEUTRAL_POSITION_MAX_X),
    yBasisPoints: spreadCoordinate(row, rowCount, DEFAULT_POSITION_MIN_Y, DEFAULT_POSITION_MAX_Y),
  }
}

/**
 * Persisted marker positions always win. These fallbacks only establish the
 * first tactical view: allied Defense groups line up left, enemy Attack
 * groups line up right, and custom rooms retain a centered grid.
 */
function defaultPosition(group: RaidGroup): RaidMapGroupPosition {
  if (props.defenseGroupIds.has(group.stableId)) {
    return linePosition(group, props.defenseGroupIds, DEFAULT_DEFENSE_POSITION_X)
  }
  if (props.attackGroupIds.has(group.stableId)) {
    return linePosition(group, props.attackGroupIds, DEFAULT_ATTACK_POSITION_X)
  }
  return neutralPosition(group)
}

function storedPosition(group: RaidGroup): RaidMapGroupPosition {
  return positionsByGroupId.value.get(group.id) ?? defaultPosition(group)
}

function displayPosition(group: RaidGroup): RaidMapGroupPosition {
  const dragging = dragState.value
  return dragging?.kind === 'group' && dragging.raidGroupId === group.id
    ? dragging.preview
    : storedPosition(group)
}

function storedTreePosition(): RaidMapTreePosition {
  return props.treePosition ?? TREE_OBJECTIVE
}

function displayTreePosition(): RaidMapTreePosition {
  const dragging = dragState.value
  return dragging?.kind === 'tree' ? dragging.preview : storedTreePosition()
}

function playerNamesForGroup(group: RaidGroup): ReadonlyArray<string> {
  const ids = new Set(group.playerIds)
  return props.players
    .filter((player) => ids.has(player.id))
    .map((player) => playerDisplayName(player, props.playerNameMode))
}

function playerNamesForTag(tagKey: string): ReadonlyArray<string> {
  return playerNamesByTagKey.value.get(tagKey) ?? []
}

function startGroupDrag(raidGroupId: string, event: PointerEvent): void {
  const map = mapRef.value
  const group = props.groups.find((candidate) => candidate.id === raidGroupId)
  if (!props.editable || map === null || group === undefined) return
  const initial = storedPosition(group)
  map.setPointerCapture(event.pointerId)
  dragState.value = {
    kind: 'group',
    raidGroupId,
    pointerId: event.pointerId,
    initial,
    preview: initial,
  }
}

function startTreeDrag(event: PointerEvent): void {
  const map = mapRef.value
  if (!props.editable || map === null) return
  const initial = storedTreePosition()
  map.setPointerCapture(event.pointerId)
  dragState.value = {
    kind: 'tree',
    pointerId: event.pointerId,
    initial,
    preview: initial,
  }
}

function onPointerMove(event: PointerEvent): void {
  const map = mapRef.value
  const dragging = dragState.value
  if (map === null || dragging === null || dragging.pointerId !== event.pointerId) return
  const bounds = map.getBoundingClientRect()
  if (bounds.width === 0 || bounds.height === 0) return
  const preview: MapPosition = {
    xBasisPoints: clampBasisPoints(((event.clientX - bounds.left) / bounds.width) * 10000),
    yBasisPoints: clampBasisPoints(((event.clientY - bounds.top) / bounds.height) * 10000),
  }
  dragState.value = dragging.kind === 'group'
    ? {
      ...dragging,
      preview: {
        raidGroupId: dragging.raidGroupId,
        ...preview,
      },
    }
    : {
      ...dragging,
      preview,
    }
}

function onPointerUp(event: PointerEvent): void {
  const dragging = dragState.value
  if (dragging === null || dragging.pointerId !== event.pointerId) return
  if (dragging.kind === 'group') {
    const group = props.groups.find((candidate) => candidate.id === dragging.raidGroupId)
    if (positionMoved(dragging.initial, dragging.preview)) {
      emit('move-group', dragging.raidGroupId, dragging.preview.xBasisPoints, dragging.preview.yBasisPoints)
    } else if (group !== undefined) {
      emit('edit-group', group.stableId)
    }
  } else if (positionMoved(dragging.initial, dragging.preview)) {
    emit('move-tree', dragging.preview.xBasisPoints, dragging.preview.yBasisPoints)
  } else {
    editTag(TREE_OBJECTIVE.tagKey)
  }
  cancelDrag()
}

function positionMoved(initial: MapPosition, preview: MapPosition): boolean {
  return Math.abs(initial.xBasisPoints - preview.xBasisPoints) >= DRAG_SAVE_THRESHOLD_BASIS_POINTS
    || Math.abs(initial.yBasisPoints - preview.yBasisPoints) >= DRAG_SAVE_THRESHOLD_BASIS_POINTS
}

function cancelDrag(): void {
  const map = mapRef.value
  const dragging = dragState.value
  if (map !== null && dragging !== null && map.hasPointerCapture(dragging.pointerId)) {
    map.releasePointerCapture(dragging.pointerId)
  }
  dragState.value = null
}

function onNudge(raidGroupId: string, nudge: MapMarkerNudge): void {
  if (!props.editable) return
  const group = props.groups.find((candidate) => candidate.id === raidGroupId)
  if (group === undefined) return
  const current = storedPosition(group)
  const next: RaidMapGroupPosition = {
    raidGroupId: group.id,
    xBasisPoints: clampBasisPoints(current.xBasisPoints + nudge.xDeltaBasisPoints),
    yBasisPoints: clampBasisPoints(current.yBasisPoints + nudge.yDeltaBasisPoints),
  }
  emit('move-group', next.raidGroupId, next.xBasisPoints, next.yBasisPoints)
}

function onTreeNudge(nudge: MapMarkerNudge): void {
  if (!props.editable) return
  const current = storedTreePosition()
  const next: RaidMapTreePosition = {
    xBasisPoints: clampBasisPoints(current.xBasisPoints + nudge.xDeltaBasisPoints),
    yBasisPoints: clampBasisPoints(current.yBasisPoints + nudge.yDeltaBasisPoints),
  }
  emit('move-tree', next.xBasisPoints, next.yBasisPoints)
}

function editGroup(group: RaidGroup): void {
  if (!props.editable) return
  emit('edit-group', group.stableId)
}

function editTag(tagKey: string): void {
  if (!props.editable) return
  emit('edit-tag', tagKey)
}
</script>
